"use client";

import { useEffect, useState, useMemo } from "react";
import { useRouter } from "next/navigation";
import {
  FileText,
  CheckCircle,
  Clock,
  XCircle,
  AlertCircle,
  Eye,
  Filter,
  Download,
  RefreshCw,
  MessageSquare,
} from "lucide-react";

import { Button } from "@/app/_module/components/ui/button";
import { Badge } from "@/app/_module/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/app/_module/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/app/_module/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/app/_module/components/ui/select";
import {
  Dialog,
  ResponsiveDialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/app/_module/components/ui/dialog";
import { Textarea } from "@/app/_module/components/ui/textarea";
import { useToast } from "@/app/_module/hooks/use-toast";
import { useUserContext } from "@/app/_module/store/contexts/userContext/userContexts";
import { adminService } from "@/app/_module/services/adminService";

// Types
interface DocumentRequest {
  id: string;
  agentName: string;
  agentId: string;
  carrierName: string;
  documentType: string;
  status: "pending" | "in_progress" | "completed" | "rejected";
  priority: "low" | "medium" | "high" | "urgent";
  createdAt: string;
  updatedAt: string;
  notes?: string;
  requestDetails?: string;
}

interface Stats {
  total: number;
  pending: number;
  inProgress: number;
  completed: number;
  rejected: number;
  byCarrier: Record<string, number>;
}

const DocumentRequestsAdmin = function () {
  const { user, isInitializing } = useUserContext();
  const router = useRouter();
  const { toast } = useToast();

  const [requests, setRequests] = useState<DocumentRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [carrierFilter, setCarrierFilter] = useState<string>("all");
  const [priorityFilter, setPriorityFilter] = useState<string>("all");

  // Dialog states
  const [selectedRequest, setSelectedRequest] = useState<DocumentRequest | null>(null);
  const [showDetailsDialog, setShowDetailsDialog] = useState(false);
  const [showNotesDialog, setShowNotesDialog] = useState(false);
  const [notes, setNotes] = useState("");
  const [updatingStatus, setUpdatingStatus] = useState(false);

  // Check admin access
  const isAdmin = useMemo(() => {
    return user?.user?.userType === "admin";
  }, [user?.user?.userType]);

  // Fetch document requests
  const fetchRequests = async () => {
    setLoading(true);
    try {
      const response = await adminService.getDocumentRequests();
      // Handle nested data structure from NestJS response
      const data = response?.data || response;
      // Transform backend response to match frontend interface
      const transformedRequests = (data || []).map((req: any) => ({
        id: req.id,
        agentName: req.userName || "Unknown Agent",
        agentId: req.userId,
        carrierName: req.carrierName || "Unknown Carrier",
        documentType: req.documentType,
        status: req.status,
        priority: req.priority === "normal" ? "medium" : req.priority,
        createdAt: req.createdAt,
        updatedAt: req.updatedAt,
        notes: req.adminNotes,
        requestDetails: req.requestReason,
      }));
      setRequests(transformedRequests);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to load document requests",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!isInitializing && !isAdmin) {
      toast({
        title: "Access Denied",
        description: "You need administrator privileges to access this page.",
        variant: "destructive",
      });
      router.push("/dashboard");
      return;
    }

    if (!isInitializing && isAdmin) {
      fetchRequests();
    }
  }, [isInitializing, isAdmin, router, toast]);

  // Calculate statistics
  const stats: Stats = useMemo(() => {
    const result: Stats = {
      total: requests.length,
      pending: 0,
      inProgress: 0,
      completed: 0,
      rejected: 0,
      byCarrier: {},
    };

    requests.forEach((req) => {
      // Count by status
      if (req.status === "pending") result.pending++;
      if (req.status === "in_progress") result.inProgress++;
      if (req.status === "completed") result.completed++;
      if (req.status === "rejected") result.rejected++;

      // Count by carrier
      if (req.carrierName) {
        result.byCarrier[req.carrierName] = (result.byCarrier[req.carrierName] || 0) + 1;
      }
    });

    return result;
  }, [requests]);

  // Filter requests
  const filteredRequests = useMemo(() => {
    return requests.filter((req) => {
      if (statusFilter !== "all" && req.status !== statusFilter) return false;
      if (carrierFilter !== "all" && req.carrierName !== carrierFilter) return false;
      if (priorityFilter !== "all" && req.priority !== priorityFilter) return false;
      return true;
    });
  }, [requests, statusFilter, carrierFilter, priorityFilter]);

  // Get unique carriers
  const carriers = useMemo(() => {
    const uniqueCarriers = new Set(requests.map((req) => req.carrierName));
    return Array.from(uniqueCarriers).sort();
  }, [requests]);

  // Priority color mapping
  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case "urgent":
        return "bg-red-100 text-red-800 border-red-300";
      case "high":
        return "bg-orange-100 text-orange-800 border-orange-300";
      case "medium":
        return "bg-yellow-100 text-yellow-800 border-yellow-300";
      case "low":
        return "bg-green-100 text-green-800 border-green-300";
      default:
        return "bg-gray-100 text-gray-800 border-gray-300";
    }
  };

  // Status badge variant
  const getStatusVariant = (status: string) => {
    switch (status) {
      case "completed":
        return "default";
      case "in_progress":
        return "secondary";
      case "pending":
        return "outline";
      case "rejected":
        return "destructive";
      default:
        return "outline";
    }
  };

  // Update request status
  const handleUpdateStatus = async (requestId: string, newStatus: string) => {
    setUpdatingStatus(true);
    try {
      await adminService.updateDocumentRequest(requestId, { status: newStatus });

      toast({
        title: "Status Updated",
        description: `Request status changed to ${newStatus.replace("_", " ")}`,
      });

      fetchRequests();
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to update request status",
        variant: "destructive",
      });
    } finally {
      setUpdatingStatus(false);
    }
  };

  // Add notes to request
  const handleAddNotes = async () => {
    if (!selectedRequest || !notes.trim()) return;

    try {
      await adminService.updateDocumentRequest(selectedRequest.id, {
        adminNotes: notes.trim(),
      });

      toast({
        title: "Notes Added",
        description: "Notes have been saved successfully",
      });

      setShowNotesDialog(false);
      setNotes("");
      fetchRequests();
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to save notes",
        variant: "destructive",
      });
    }
  };

  // View request details
  const handleViewDetails = (request: DocumentRequest) => {
    setSelectedRequest(request);
    setShowDetailsDialog(true);
  };

  // Open notes dialog
  const handleOpenNotes = (request: DocumentRequest) => {
    setSelectedRequest(request);
    setNotes(request.notes || "");
    setShowNotesDialog(true);
  };

  // Export to CSV
  const handleExport = () => {
    try {
      const headers = [
        "Agent Name",
        "Carrier",
        "Document Type",
        "Status",
        "Priority",
        "Created Date",
        "Notes",
      ];

      const csvRows = [headers.join(",")];

      filteredRequests.forEach((req) => {
        const row = [
          `"${req.agentName}"`,
          `"${req.carrierName}"`,
          `"${req.documentType}"`,
          req.status,
          req.priority,
          new Date(req.createdAt).toLocaleDateString(),
          `"${(req.notes || "").replace(/"/g, '""')}"`,
        ];
        csvRows.push(row.join(","));
      });

      const csvContent = csvRows.join("\n");
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);

      // Format date in local timezone to avoid UTC shift
      const now = new Date();
      const timestamp = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
      const fileName = `document-requests-${timestamp}.csv`;

      link.setAttribute("href", url);
      link.setAttribute("download", fileName);
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      toast({
        title: "Export Successful",
        description: `Data exported as ${fileName}`,
      });
    } catch (error) {

      toast({
        title: "Export Failed",
        description: "Failed to export data",
        variant: "destructive",
      });
    }
  };

  if (isInitializing || !isAdmin) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4" />
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto py-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Document Requests</h1>
          <p className="text-muted-foreground mt-1">
            Manage and track document requests from agents
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={fetchRequests}
            disabled={loading}
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </Button>
          <Button variant="outline" size="sm" onClick={handleExport}>
            <Download className="h-4 w-4 mr-2" />
            Export
          </Button>
        </div>
      </div>

      {/* Statistics Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Requests</CardTitle>
            <FileText className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.total}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Pending</CardTitle>
            <Clock className="h-4 w-4 text-yellow-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.pending}</div>
            <p className="text-xs text-muted-foreground mt-1">
              + {stats.inProgress} in progress
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Completed</CardTitle>
            <CheckCircle className="h-4 w-4 text-green-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.completed}</div>
            <p className="text-xs text-muted-foreground mt-1">
              {stats.total > 0
                ? `${((stats.completed / stats.total) * 100).toFixed(1)}%`
                : "0%"}{" "}
              completion rate
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">By Carrier</CardTitle>
            <AlertCircle className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="space-y-1">
              {Object.entries(stats.byCarrier)
                .sort(([, a], [, b]) => b - a)
                .slice(0, 2)
                .map(([carrier, count]) => (
                  <div key={carrier} className="flex justify-between text-sm">
                    <span className="text-muted-foreground truncate">{carrier}</span>
                    <span className="font-medium">{count}</span>
                  </div>
                ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Filter className="h-5 w-5" />
            Filters
          </CardTitle>
          <CardDescription>Filter requests by status, carrier, or priority</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-3">
            <div>
              <label className="text-sm font-medium mb-2 block">Status</label>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="in_progress">In Progress</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                  <SelectItem value="rejected">Rejected</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="text-sm font-medium mb-2 block">Carrier</label>
              <Select value={carrierFilter} onValueChange={setCarrierFilter}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Carriers</SelectItem>
                  {carriers.map((carrier) => (
                    <SelectItem key={carrier} value={carrier}>
                      {carrier}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="text-sm font-medium mb-2 block">Priority</label>
              <Select value={priorityFilter} onValueChange={setPriorityFilter}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Priorities</SelectItem>
                  <SelectItem value="urgent">Urgent</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="low">Low</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {(statusFilter !== "all" ||
            carrierFilter !== "all" ||
            priorityFilter !== "all") && (
            <div className="mt-4">
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setStatusFilter("all");
                  setCarrierFilter("all");
                  setPriorityFilter("all");
                }}
              >
                <XCircle className="h-4 w-4 mr-2" />
                Clear Filters
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Requests Table */}
      <Card>
        <CardHeader>
          <CardTitle>Document Requests</CardTitle>
          <CardDescription>
            Showing {filteredRequests.length} of {requests.length} requests
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <div className="text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4" />
                <p>Loading requests...</p>
              </div>
            </div>
          ) : filteredRequests.length === 0 ? (
            <div className="text-center py-8">
              <FileText className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-lg font-medium">No requests found</p>
              <p className="text-sm text-muted-foreground mt-1">
                {requests.length === 0
                  ? "No document requests have been submitted yet"
                  : "Try adjusting your filters"}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Agent</TableHead>
                    <TableHead>Carrier</TableHead>
                    <TableHead>Document Type</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Priority</TableHead>
                    <TableHead>Created</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredRequests.map((request) => (
                    <TableRow key={request.id}>
                      <TableCell className="font-medium">{request.agentName}</TableCell>
                      <TableCell>{request.carrierName}</TableCell>
                      <TableCell>{request.documentType}</TableCell>
                      <TableCell>
                        <Select
                          value={request.status}
                          onValueChange={(value) =>
                            handleUpdateStatus(request.id, value)
                          }
                          disabled={updatingStatus}
                        >
                          <SelectTrigger className="w-[140px]">
                            <Badge variant={getStatusVariant(request.status)}>
                              {request.status.replace("_", " ")}
                            </Badge>
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="pending">Pending</SelectItem>
                            <SelectItem value="in_progress">In Progress</SelectItem>
                            <SelectItem value="completed">Completed</SelectItem>
                            <SelectItem value="rejected">Rejected</SelectItem>
                          </SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell>
                        <Badge
                          className={getPriorityColor(request.priority)}
                          variant="outline"
                        >
                          {request.priority}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {new Date(request.createdAt).toLocaleDateString()}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex flex-wrap justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleViewDetails(request)}
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleOpenNotes(request)}
                          >
                            <MessageSquare className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Details Dialog */}
      <Dialog open={showDetailsDialog} onOpenChange={setShowDetailsDialog}>
        <ResponsiveDialogContent size="2xl">
          <DialogHeader>
            <DialogTitle>Request Details</DialogTitle>
            <DialogDescription>
              Full details for document request #{selectedRequest?.id}
            </DialogDescription>
          </DialogHeader>
          {selectedRequest && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-muted-foreground">
                    Agent Name
                  </label>
                  <p className="text-sm font-medium mt-1">{selectedRequest.agentName}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground">
                    Carrier
                  </label>
                  <p className="text-sm font-medium mt-1">
                    {selectedRequest.carrierName}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground">
                    Document Type
                  </label>
                  <p className="text-sm font-medium mt-1">
                    {selectedRequest.documentType}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground">
                    Status
                  </label>
                  <div className="mt-1">
                    <Badge variant={getStatusVariant(selectedRequest.status)}>
                      {selectedRequest.status.replace("_", " ")}
                    </Badge>
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground">
                    Priority
                  </label>
                  <div className="mt-1">
                    <Badge
                      className={getPriorityColor(selectedRequest.priority)}
                      variant="outline"
                    >
                      {selectedRequest.priority}
                    </Badge>
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground">
                    Created
                  </label>
                  <p className="text-sm font-medium mt-1">
                    {new Date(selectedRequest.createdAt).toLocaleString()}
                  </p>
                </div>
              </div>
              {selectedRequest.requestDetails && (
                <div>
                  <label className="text-sm font-medium text-muted-foreground">
                    Request Details
                  </label>
                  <p className="text-sm mt-1 p-3 bg-muted rounded-md">
                    {selectedRequest.requestDetails}
                  </p>
                </div>
              )}
              {selectedRequest.notes && (
                <div>
                  <label className="text-sm font-medium text-muted-foreground">
                    Notes
                  </label>
                  <p className="text-sm mt-1 p-3 bg-muted rounded-md">
                    {selectedRequest.notes}
                  </p>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDetailsDialog(false)}>
              Close
            </Button>
          </DialogFooter>
        </ResponsiveDialogContent>
      </Dialog>

      {/* Notes Dialog */}
      <Dialog open={showNotesDialog} onOpenChange={setShowNotesDialog}>
        <ResponsiveDialogContent size="md">
          <DialogHeader>
            <DialogTitle>Add Notes</DialogTitle>
            <DialogDescription>
              Add or update notes for this document request
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium mb-2 block">Notes</label>
              <Textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Enter notes about this request..."
                rows={5}
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowNotesDialog(false);
                setNotes("");
              }}
            >
              Cancel
            </Button>
            <Button onClick={handleAddNotes} disabled={!notes.trim()}>
              Save Notes
            </Button>
          </DialogFooter>
        </ResponsiveDialogContent>
      </Dialog>
    </div>
  );
};

export default DocumentRequestsAdmin;
